/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Button,
  Cell,
  Grid,
  HFlow,
  Icon,
  InfoLabel,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useTheme,
  VFlow,
} from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { calculateAge } from 'util/date/calculateAge'
import { humanizeAge } from 'util/date/humanize-age'
import { parseNumber } from 'util/number'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { PLACE_HOLDER_PUERICULTURA, PuericulturaModel } from '../../objetivo/puericultura/model'
import { handlePrint } from './AcompanhamentoPuericulturaSection'
import { DesenvolvimentoCriancaAcompanhamentoPuericultura } from './DesenvolvimentoCriancaAcompanhamentoPuericultura'
import { MChatAcompanhamentoPuericultura } from './MChatAcompanhamentoPuericultura'
import { AcompanhamentoPuericulturaModel } from './model'
import { PreNatalPartoNascimentoAcompanhamentoPuericultura } from './PreNatalPartoNascimentoAcompanhamentoPuericultura'
import { tipoAleitamentoToString, ultimoAtendimentoLabel, vacinacaoToString } from './util/convertToLabel'
import { getHumanizedIdadeCorrigida } from './util/IdadeCorrigida'

interface AcompanhamentoPuericulturaModalProps {
  acompanhamentoPuericultura: AcompanhamentoPuericulturaModel
  atendimentoPuericultura?: PuericulturaModel
  vacinacaoEmDiaAtendimento?: boolean
  isAntecedentes?: Boolean
  closeModal(): void
  dataAtendimento: Instant
  cidadao: Pick<CidadaoAtendimento, 'id' | 'dataNascimento' | 'sexo'>
  prontuarioId: ID
  atendimentoId?: ID
}

export function AcompanhamentoPuericulturaModal(props: Readonly<AcompanhamentoPuericulturaModalProps>) {
  const {
    acompanhamentoPuericultura,
    closeModal,
    vacinacaoEmDiaAtendimento,
    atendimentoPuericultura,
    isAntecedentes,
    dataAtendimento,
    cidadao,
    prontuarioId,
    atendimentoId,
  } = props
  const theme = useTheme()
  const { M_CHAT_ENABLED } = useFlags()

  const { handleImpressaoPath } = useHandleImpressao()

  const idadeCidadao = humanizeAge(cidadao?.dataNascimento, dataAtendimento)
  const { ageInMonths: idadeEmMeses } = calculateAge(cidadao?.dataNascimento, dataAtendimento)
  const { tipoAleitamentoMaterno, preNatalPartoNascimento, vacinacaoEmDia } = acompanhamentoPuericultura || {}
  const showMChat = idadeEmMeses >= 16 && M_CHAT_ENABLED

  const antecedenteNascimento = acompanhamentoPuericultura?.preNatalPartoNascimento
  const idadeCorrigida = getHumanizedIdadeCorrigida(
    parseNumber(antecedenteNascimento?.idadeGestacionalSemanas),
    parseNumber(antecedenteNascimento?.idadeGestacionalDias),
    cidadao?.dataNascimento
  )

  return (
    <Modal
      style={css`
        width: 60rem;
      `}
      open
      onClose={closeModal}
    >
      <ModalHeader
        title='Acompanhamento da criança'
        subtitle={idadeCidadao + (idadeEmMeses >= 12 && !isAntecedentes ? ` (${idadeEmMeses} meses)` : '')}
      />
      <ModalBody>
        <VFlow>
          <PreNatalPartoNascimentoAcompanhamentoPuericultura
            preNatalPartoNascimento={preNatalPartoNascimento}
            prenatalPartoNascimentoAtendimento={atendimentoPuericultura?.prenatalPartoNascimento}
            isAntecedentes={isAntecedentes}
          />

          <Grid
            style={css`
              border-top: 1px solid ${theme.pallete.divider};
            `}
          >
            <Cell size={3}>
              <InfoLabel title='Vacinação em dia' placeholder={PLACE_HOLDER_PUERICULTURA}>
                {vacinacaoToString(vacinacaoEmDiaAtendimento, dataAtendimento) ||
                  vacinacaoToString(vacinacaoEmDia?.valorMedicao as boolean, vacinacaoEmDia?.dataMedicao)}
              </InfoLabel>
            </Cell>
            <Cell size={3}>
              <InfoLabel title='Aleitamento materno' placeholder={PLACE_HOLDER_PUERICULTURA}>
                {tipoAleitamentoToString(atendimentoPuericultura?.tipoAleitamento, dataAtendimento) ||
                  tipoAleitamentoToString(tipoAleitamentoMaterno?.tipo, tipoAleitamentoMaterno?.dataInicioAtendimento)}
              </InfoLabel>
            </Cell>
            {idadeCorrigida && (
              <Cell size={3}>
                <InfoLabel title='Idade corrigida'>{idadeCorrigida}</InfoLabel>
              </Cell>
            )}
            <Cell size={6}>
              <InfoLabel title='Última consulta'>
                {ultimoAtendimentoLabel(acompanhamentoPuericultura?.ultimoAtendimentoPuericultura)}
              </InfoLabel>
            </Cell>
            <Cell size={12}>
              <DesenvolvimentoCriancaAcompanhamentoPuericultura
                acompanhamentoPuericultura={acompanhamentoPuericultura}
                atendimentoPuericultura={atendimentoPuericultura}
                dataAtendimento={dataAtendimento}
                cidadao={cidadao}
              />
            </Cell>
            {showMChat && (
              <Cell size={12}>
                <MChatAcompanhamentoPuericultura
                  registrosMChat={acompanhamentoPuericultura.registrosMChat}
                  cidadaoDataNascimento={cidadao?.dataNascimento}
                  cidadaoId={cidadao?.id}
                />
              </Cell>
            )}
          </Grid>
        </VFlow>
      </ModalBody>
      <ModalFooter>
        <HFlow justifyContent='flex-end'>
          <Button kind='primary' onClick={() => handlePrint(handleImpressaoPath, prontuarioId, atendimentoId)}>
            <HFlow>
              <Icon icon='printerOutline' />
              Imprimir acompanhamento
            </HFlow>
          </Button>
        </HFlow>
      </ModalFooter>
    </Modal>
  )
}
